type Installment = {
  dueDate: Date,
  days: number,
  amount: number,
  interest: number,
  amortization: number,
  iofAmount: number
}

type CreditSimulatorValues = {
  requestedAmountCents: number,
  numberOfInstallments: number,
  createdAt: Date
}

type SimulateLoanValues = CreditSimulatorValues & {
  extraDays: number,
  monthlyInterestRate: number,
  mutualFeeRate: number
}

type CalculateInstallmentsValues = {
  financedAmountWithoutIof: number,
  monthlyInterestRate: number,
  numberOfInstallments: number,
  extraDays: number
}

type RoundFinancedAmountByQuotaValues = {
  financedAmount: number,
  quotaValue: number
}

type PmtFormulaValues = {
  financedAmount: number,
  numberOfInstallments: number,
  monthlyInterestRate: number
}

type IpmtFormulaValues = {
  financedAmount: number,
  monthlyInterestRate: number,
  pmt: number,
  period: number
}

type DiscoverExpenseToRoundFinancedAmountValues = {
  financedAmountWithoutIof: number,
  monthlyInterestRate: number,
  numberOfInstallments: number,
  extraDays: number,
  roundedFinancedAmount: number
}

type ToOptimizeValues = {
  extraExpense: number,
  financedAmountWithoutIof: number,
  monthlyInterestRate: number,
  numberOfInstallments: number,
  extraDays: number,
  roundedFinancedAmount: number
}

type BisectValues = {
  a: number,
  b: number,
  financedAmountWithoutIof: number,
  monthlyInterestRate: number,
  numberOfInstallments: number,
  extraDays: number,
  roundedFinancedAmount: number
}

let installments: Installment[] = []

export const creditSimulator = (values: CreditSimulatorValues) => {
  const requestedAmountCents = values.requestedAmountCents / 100
  const numberOfInstallments = values.numberOfInstallments
  const extraDays = 0
  const monthlyInterestRate = 0.02
  const mutualFeeRate = 0.05
  const createdAt = values.createdAt

  const installmentValue = Number(simulateLoan({ requestedAmountCents, numberOfInstallments, extraDays, monthlyInterestRate, mutualFeeRate, createdAt }).toFixed(2))
  const totalValue = (installmentValue * numberOfInstallments).toFixed(2)
  return { installmentValue, totalValue }
}

function simulateLoan ({ requestedAmountCents, numberOfInstallments, extraDays, monthlyInterestRate, mutualFeeRate, createdAt }: SimulateLoanValues): number {
  createdAt = new Date()
  createdAt.setHours(0, 0, 0, 0)

  for (let i = 1; i <= numberOfInstallments; i++) {
    const installment = {
      dueDate: new Date(createdAt.getFullYear(), (createdAt.getMonth() + i), createdAt.getDate() + extraDays, 0, 0, 0, 0),
      days: Math.round(((Number(new Date(createdAt.getFullYear(), (createdAt.getMonth() + i), createdAt.getDate() + extraDays, 0, 0, 0, 0)) - Number(createdAt))) / (1000 * 60 * 60 * 24)),
      amount: 0,
      interest: 0,
      amortization: 0,
      iofAmount: 0
    }
    installments.push(installment)
  }

  const taxes = {
    mutualFee: requestedAmountCents * mutualFeeRate,
    financialFee: ((requestedAmountCents < 3000000) ? (requestedAmountCents * 0.01) : ((requestedAmountCents < 5000000) ? (requestedAmountCents * 0.008) : ((requestedAmountCents < 10000000) ? (requestedAmountCents * 0.007) : ((requestedAmountCents < 15000000) ? (requestedAmountCents * 0.006) : (requestedAmountCents * 0.005))))),
    operationalCosts: numberOfInstallments * 2.50,
    iofAmount: 0,
    quotaExtra: 0
  }

  let financedAmountWithoutIof = requestedAmountCents + taxes.mutualFee + taxes.financialFee + taxes.operationalCosts
  let iofAmount = calculateInstallments({ financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays })
  let financedAmount = financedAmountWithoutIof + iofAmount
  const quotaValue = 5
  const roundedFinancedAmount = roundFinancedAmountByQuota({ financedAmount, quotaValue })

  if (financedAmount === roundedFinancedAmount) {
    return (installments[0].amount)
  }

  const extraExpense = discoverExpenseToRoundFinancedAmount({ financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount }) / 100
  taxes.quotaExtra = extraExpense
  financedAmountWithoutIof += taxes.quotaExtra
  iofAmount = calculateInstallments({ financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays })
  taxes.iofAmount = iofAmount
  financedAmount = financedAmountWithoutIof + iofAmount

  if (extraDays > 0) {
    const dailyInterestRate = Math.pow(1 + monthlyInterestRate, 1 / 30) - 1
    const extraDaysInterestAmount = financedAmount * dailyInterestRate * extraDays
    financedAmount += extraDaysInterestAmount
  }

  const installmentAmount = (installments[0].amount)
  installments = []
  return installmentAmount
}

function calculateInstallments ({ financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments }: CalculateInstallmentsValues): number {
  let iofAmount = financedAmountWithoutIof * 0.0038

  for (let i = 0; i < numberOfInstallments; i++) {
    installments[i].amount = Math.round((((pmtFormula({ financedAmount: financedAmountWithoutIof, numberOfInstallments, monthlyInterestRate })) + Number.EPSILON) * 100)) / 100
    installments[i].interest = Math.round(((ipmtFormula({
      financedAmount: -financedAmountWithoutIof,
      monthlyInterestRate,
      pmt: installments[i].amount,
      period: i
    })) + Number.EPSILON) * 100) / 100
    installments[i].amortization = Math.round(((installments[i].amount - installments[i].interest) + Number.EPSILON) * 100) / 100

    if (installments[i].days <= 365) {
      installments[i].iofAmount = installments[i].amortization * installments[i].days * 0.000041
      iofAmount += installments[i].iofAmount
    } else {
      installments[i].iofAmount = installments[i].amortization * 365 * 0.000041
      iofAmount += installments[i].iofAmount
    }
  }

  return (Math.round(((iofAmount) + Number.EPSILON) * 100) / 100)
}

function roundFinancedAmountByQuota ({ financedAmount, quotaValue }: RoundFinancedAmountByQuotaValues): number {
  const numberOfQuotas = Math.ceil(financedAmount / quotaValue)
  return numberOfQuotas * quotaValue
}

function pmtFormula ({ financedAmount, numberOfInstallments, monthlyInterestRate }: PmtFormulaValues): number {
  return ((financedAmount * monthlyInterestRate) / (1 - (1 / (Math.pow((1 + monthlyInterestRate), numberOfInstallments)))))
}

function ipmtFormula ({ financedAmount, monthlyInterestRate, pmt, period }: IpmtFormulaValues): number {
  const tmp = Math.pow(1 + monthlyInterestRate, period)
  return (0 - (financedAmount * tmp * monthlyInterestRate + pmt * (tmp - 1)))
}

function discoverExpenseToRoundFinancedAmount ({ financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount }: DiscoverExpenseToRoundFinancedAmountValues): number {
  const extraExpenseAmount = bisect({
    a: 0,
    b: 500,
    financedAmountWithoutIof,
    monthlyInterestRate,
    numberOfInstallments,
    extraDays,
    roundedFinancedAmount
  })
  return Math.round(extraExpenseAmount)
}

function toOptimize ({ extraExpense, financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount }: ToOptimizeValues): number {
  const iofAmount = calculateInstallments({
    financedAmountWithoutIof: financedAmountWithoutIof + parseFloat((extraExpense / 100).toFixed(2)),
    monthlyInterestRate,
    numberOfInstallments,
    extraDays
  })
  const financedAmount = financedAmountWithoutIof + iofAmount + parseFloat((extraExpense / 100).toFixed(2))
  return financedAmount - roundedFinancedAmount
}

function bisect ({ a, b, financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount }: BisectValues): number {
  const maxIterations = 100
  const tolerance = 1e-10
  const fA = toOptimize({ extraExpense: a, financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount })
  const fB = toOptimize({ extraExpense: b, financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount })
  let delta = b - a
  if (fA * fB > 0) {
    throw new Error('Initial bisect points must have opposite signs')
  }
  if (fA === 0) return a
  if (fB === 0) return b
  for (let i = 0; i < maxIterations; ++i) {
    delta /= 2
    const mid = a + delta
    const fMid = toOptimize({ extraExpense: mid, financedAmountWithoutIof, monthlyInterestRate, numberOfInstallments, extraDays, roundedFinancedAmount })
    if (fMid * fA >= 0) {
      a = mid
    }
    if ((Math.abs(delta) < tolerance) || (fMid === 0)) {
      return mid
    }
  }
  return a + delta
}
